import React, { useState, useEffect, useContext } from "react";
import { Avatar } from "antd";
import EXP_PLACEHOLDER from "assets/icons/DetailedProfile/experiencePlaceholder.svg";
import ExperienceForm from "../../../../../Auth/Profile/partials/ExperienceForm";
import SameExperienceEditForm from "./SameExperienceEditForm";
import ExperienceComment from "./ExperienceComment";
import _ from "lodash";
import { EditContext } from "containers/Candidates/Profile/Context/EditContext";

interface PropsType {
  experience: any;
  isRecruiterPage?: boolean;
  candidateId: any;
}

const convertToYear = (year: string | null | undefined): number => {
  if (!year) return new Date().getFullYear();
  if (year === "Present" || year === "present") return new Date().getFullYear();

  // Handle year strings that might have formats like "2020", "2020-2021", "2020-01", etc.
  const yearStr = String(year).trim();
  const yearMatch = yearStr.match(/^\d{4}/);

  if (yearMatch) {
    const parsedYear = parseInt(yearMatch[0], 10);
    // Validate it's a reasonable year (between 1900 and current year + 10)
    const currentYear = new Date().getFullYear();
    if (
      !isNaN(parsedYear) &&
      parsedYear >= 1900 &&
      parsedYear <= currentYear + 10
    ) {
      return parsedYear;
    }
  }

  // Fallback to current year if parsing fails
  return new Date().getFullYear();
};

const SingleExperience: React.FC<PropsType> = ({
  experience,
  candidateId,
  isRecruiterPage,
}) => {
  const [imgError, setImgError] = useState(false);
  const editContext = useContext<any>(EditContext);
  const [logo, setLogo] = useState(experience?.logo);
  const calculateTotalYearsSpent = (experience: any): number | null => {
    try {
      // Validate experience data exists
      if (!experience || (!experience.startYear && !experience?.mergedData)) {
        return null;
      }

      const experiences = [experience, ...(experience?.mergedData || [])];

      // Filter out invalid experiences
      const validExperiences = experiences.filter(
        (exp: any) => exp && (exp.startYear || exp.endYear)
      );

      if (validExperiences.length === 0) {
        return null;
      }

      // Convert experiences to periods with start and end years
      const periods = validExperiences.map((exp: any) => ({
        start: convertToYear(exp.startYear),
        end: convertToYear(exp.endYear || "Present"),
      }));

      // Sort periods by start year
      periods.sort((a, b) => a.start - b.start);

      // Merge overlapping periods and calculate total years
      let totalYears = 0;
      const mergedPeriods: Array<{ start: number; end: number }> = [];

      for (const period of periods) {
        if (mergedPeriods.length === 0) {
          mergedPeriods.push({ ...period });
        } else {
          const lastPeriod = mergedPeriods[mergedPeriods.length - 1];

          // Check if current period overlaps with or is adjacent to the last period
          // (adjacent means end year + 1 = start year, e.g., 2016 and 2017 are adjacent)
          if (period.start <= lastPeriod.end + 1) {
            // Merge: extend the last period's end if current period ends later
            if (period.end > lastPeriod.end) {
              lastPeriod.end = period.end;
            }
          } else {
            // No overlap: add as a new period
            mergedPeriods.push({ ...period });
          }
        }
      }

      // Calculate total years from merged periods
      // Using endYear - startYear (not inclusive of both endpoints)
      for (const period of mergedPeriods) {
        const duration = period.end - period.start;
        if (!isNaN(duration) && duration > 0) {
          totalYears += duration;
        }
      }

      // Return null for invalid results
      if (isNaN(totalYears) || totalYears <= 0) {
        return null;
      }

      return totalYears;
    } catch (error) {
      console.error("Error calculating total years spent:", error);
      return null;
    }
  };

  useEffect(() => {
    if (!experience?.logo) {
      setImgError(true);
    }
  }, [experience]);

  useEffect(() => {
    if (experience?.logo?.length > 4) {
      setImgError(false);
      setLogo(experience?.logo);
    }
  }, [experience?.logo]);

  const handleImageError = () => {
    setImgError(true);
    return false;
  };

  const Styles = {
    root: {
      paddingLeft: 0,
      display: "inline-block",
      maxWidth: 500,
      flex: "85%",
    },
    wrapper: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: 26,
      marginTop: "-8px",
    },
    year: {
      fontSize: 14,
      marginTop: 0,
      marginBottom: 0,
      color: "#495057",
    },
    position: {
      fontWeight: 500,
      fontSize: 14,
      marginTop: 0,
      marginBottom: 0,
      color: "rgb(0 0 0 / 85%)",
    },
    company: {
      fontWeight: 600,
      fontSize: 16,
      marginTop: 0,
      marginBottom: 0,
      color: "black",
    },
    location: {
      fontWeight: 400,
      fontSize: 14,
      marginTop: 0,
      marginBottom: 0,
      color: "#495057",
    },
    avatar: {
      backgroundColor: "#F5F7F9",
    },
    totalYears: {
      fontSize: "14px",
      fontWeight: "400",
      color: "rgba(0,0,0,.5)",
      marginTop: "-1px",
    },
    placeholderAvatar: {
      backgroundColor: "#ccc",
      marginRight: "2px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  };

  const totalYearsSpent: number | null = calculateTotalYearsSpent(experience);

  if (experience?.current) {
    experience.endYear = "Present";
  }

  if (experience?.mergedData && !editContext?.edit) {
    experience.mergedData = experience.mergedData.sort((s: any) =>
      s?.current ? -1 : 1
    );
  }

  experience = _.cloneDeep(experience);

  return (
    <div
      style={{
        ...Styles.wrapper,
        flexDirection: editContext?.edit ? "column" : "row",
        alignItems: editContext?.edit ? "start" : "end",
      }}
    >
      {!editContext?.edit && (
        <div style={{ ...Styles.root, wordWrap: "break-word" }}>
          <div
            style={{ display: "flex", justifyContent: "start", gap: "16px" }}
          >
            {!imgError ? (
              <Avatar
                style={{
                  ...Styles.avatar,
                  backgroundImage: `url(${logo})`,
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  objectFit: "cover",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "3px",
                  margin: "5px",
                  flexShrink: "0",
                }}
                size={48}
                // src={logo}
                onError={handleImageError}
              />
            ) : (
              <span
                style={{
                  ...Styles.placeholderAvatar,
                  flexShrink: 0,
                  height: "48px",
                  width: "48px",
                  margin: "5px",
                  backgroundColor: "#f9f6f7",
                  borderRadius: "50%",
                }}
              >
                <img
                  style={{
                    height: "30px",
                    width: "30px",
                    background: "#f9f6f7",
                  }}
                  src={EXP_PLACEHOLDER}
                  alt="placeholder"
                />
              </span>
            )}
            <div style={{ marginLeft: "-8px", marginTop: "6px" }}>
              <p style={Styles.company}>{experience?.company}</p>
              {!experience?.mergedData && (
                <>
                  <span
                    style={{
                      color: "rgba(0,0,0,0.5)",
                      fontSize: "14px",
                      fontWeight: "400",
                    }}
                  >
                    {experience.startYear} {experience.endYear && "-"}{" "}
                    {experience?.endYear}
                  </span>
                  <p style={{ ...Styles.position }}>{experience?.position}</p>
                  <div className="">
                    <span
                      style={{
                        color: "#424952",
                        fontSize: "14px",
                        fontWeight: "400",
                      }}
                    >
                      {experience?.location?.city &&
                      experience?.location?.country
                        ? `${experience?.location?.city}, ${experience?.location?.country}`
                        : experience?.location?.city &&
                          !experience?.location?.country
                        ? `${experience?.location?.city}`
                        : ""}
                    </span>
                  </div>
                  {/* Add comments for main experience when no merged data */}
                  <ExperienceComment experience={experience} />
                </>
              )}

              {totalYearsSpent !== null &&
                totalYearsSpent !== undefined &&
                !isNaN(totalYearsSpent) &&
                totalYearsSpent > 0 &&
                experience?.mergedData && (
                  <p style={Styles.totalYears}>{totalYearsSpent} years</p>
                )}

              {experience?.mergedData?.length > 0 &&
                experience.mergedData.map((d: any, i: any) => {
                  if (d?.current) {
                    d.endYear = "Present";
                  }
                  if (d.position !== experience.position || true) {
                    return (
                      <div key={i}>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            marginTop: "20px",
                            marginLeft: "-40px",
                          }}
                        >
                          <div style={{ marginTop: "10px" }}>
                            <div
                              style={{
                                height: "9px",
                                width: "9px",
                                backgroundColor: "#418394",
                                borderRadius: "100%",
                              }}
                            />
                            <div
                              style={{
                                height: "36px",
                                width: "1.2px",
                                backgroundColor: "#418394",
                                marginTop: "-4px",
                                marginBottom: "-4px",
                                marginLeft: "4.1px",
                              }}
                            />
                            <div
                              style={{
                                height: "9px",
                                width: "9px",
                                backgroundColor: "#418394",
                                borderRadius: "100%",
                              }}
                            />
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                              marginTop: "1px",
                              marginLeft: "16px",
                            }}
                          >
                            <span
                              style={{
                                color: "rgba(0,0,0,0.5)",
                                fontSize: "14px",
                                fontWeight: "400",
                              }}
                            >
                              {d.startYear} - {d.endYear}
                            </span>
                            <div style={Styles.position}>{d.position}</div>
                            <div className="">
                              <span
                                style={{
                                  color: "#424952",
                                  fontSize: "14px",
                                  fontWeight: "400",
                                }}
                              >
                                {d?.location?.city && d?.location?.country
                                  ? `${d?.location?.city}, ${d?.location?.country}`
                                  : d?.location?.city && !d?.location?.country
                                  ? `${d?.location?.city}`
                                  : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Add comments for each merged experience */}
                        <ExperienceComment experience={d} />
                      </div>
                    );
                  }
                  // return null;
                })}
            </div>
          </div>
        </div>
      )}
      {editContext?.edit ? (
        <>
          <ExperienceForm experience={experience} candidateId={candidateId} />
          {experience?.mergedData?.length > 0 &&
            experience.mergedData.map((d: any, i: any) => {
              d = _.cloneDeep(d);
              if (d?._id == experience?._id) {
                return null;
              }
              return (
                <SameExperienceEditForm key={i} experience={d} index={i} />
              );
            })}
        </>
      ) : (
        <></>
      )}
      <div style={{ flex: 1 }} />
    </div>
  );
};

export default SingleExperience;
